import { useMemo } from 'react';

import { collection, doc, DocumentReference } from 'firebase/firestore';

import { useDocumentData } from 'react-firebase-hooks/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Membership from '../types/Membership';
import Person, { PERSON_STATUSES } from '../types/Person';
import PersonDetails from '../types/PersonDetails';

export default function usePersonDetails(person: Person | undefined, personMemberships: Membership[] = []) {
  const { isGod } = useAuth();
  const { canViewPersonDetails } = useData();
  const { userState } = useData();
  const personDetailsRef = useMemo(() => {
    if (!person) return undefined;

    if (!canViewPersonDetails(person, personMemberships) && (!isGod || (person.status !== PERSON_STATUSES.ACTIVE && person.status !== PERSON_STATUSES.DEPRECATED_ACTIVE))) return null;

    return doc(collection(firestore, 'profile', person.itemId, 'details'), person.itemId) as DocumentReference<PersonDetails>;
  }, [person, personMemberships, canViewPersonDetails, isGod]);

  const [personDetails, isPersonDetailsLoading, personDetailsError] = useDocumentData<PersonDetails>(personDetailsRef);

  if (person && (!canViewPersonDetails(person, personMemberships) && (!isGod || (person.status !== PERSON_STATUSES.ACTIVE && person.status !== PERSON_STATUSES.DEPRECATED_ACTIVE)))) {
    return {
      personDetails: null,
      isPersonDetailsLoading: false,
      personDetailsError: new Error('Person is not active or is a test person')
    };
  }

  if (userState.status === 'logged_out') {
    return {
      personDetails: null,
      isPersonDetailsLoading: false,
      personDetailsError: new Error('User must be logged in to view person details')
    };
  }

  return {
    personDetails: personDetails,
    isPersonDetailsLoading: isPersonDetailsLoading ||
      (userState.status === 'authenticating_user') ||
      (userState.status === 'authenticating_profile') ||
      (userState.status === 'authenticating_memberships'),
    personDetailsError
  };
}
